import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SEV_CLS = {
  INFO: "bg-emerald-100 text-emerald-700",
  WARNING: "bg-amber-100 text-amber-700",
  CRITICAL: "bg-red-100 text-red-700",
};
const UAT_CLS = {
  passed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  blocked: "bg-amber-100 text-amber-700",
  not_tested: "bg-gray-100 text-gray-500",
};

function Sev({ s }) {
  return <Badge className={`${SEV_CLS[s] || "bg-gray-100"} border-0`} data-testid={`sev-${s}`}>{s}</Badge>;
}

function HealthTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/ops/health").then(({ data }) => setData(data)).catch(() => toast.error("Health fetch failed"));
  }, []);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="ops-health">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Overall</h2>
        <Sev s={data.overall} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(data.components).map(([name, c]) => (
          <Card key={name} className="p-4 flex items-center justify-between" data-testid={`health-${name}`}>
            <div>
              <p className="font-medium text-sm">{name}</p>
              <p className="text-xs text-gray-500">{c.detail}</p>
            </div>
            <Sev s={c.severity} />
          </Card>
        ))}
      </div>
      <Card className="p-4" data-testid="ops-metrics">
        <p className="font-medium text-sm mb-2">Metrics</p>
        <pre className="text-xs text-gray-600 overflow-auto max-h-72">{JSON.stringify(data.metrics, null, 2)}</pre>
      </Card>
    </div>
  );
}

const RUNNERS = [
  { provider: "alpaca", label: "Alpaca (Paper)", note: "Runs the 11-step UAT against the real Alpaca Paper API (unfillable $1 AAPL limit order, then cancel). Paper only — no live routing." },
  { provider: "tradestation", label: "TradeStation (SIM)", note: "Sandbox/SIM only. Without configured credentials all 11 steps record as blocked with guidance." },
];

function UatRunner({ onDone }) {
  const [confirming, setConfirming] = useState(null);
  const [running, setRunning] = useState(null);
  const [inFlight, setInFlight] = useState([]);
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get("/ops/uat/running").then(({ data }) => setInFlight(data.running || [])).catch(() => {});
  }, []);
  const run = async (provider) => {
    setConfirming(null);
    setRunning(provider);
    setResult(null);
    try {
      const { data } = await api.post(`/broker/${provider}/uat/run`, {}, { timeout: 240000 });
      setResult({ provider, ...data });
      if (data.ran === false) toast.warning(`${provider} UAT blocked: ${data.reason}`);
      else toast.success(`${provider} UAT complete — ${data.passed ?? 0}/${data.total ?? 11} passed`);
    } catch (e) {
      if (e.response?.status === 409) toast.error("A UAT run for this provider is already in progress");
      else toast.error(e.response?.data?.detail || "UAT run failed");
    } finally {
      setRunning(null);
      onDone();
    }
  };
  const target = RUNNERS.find((r) => r.provider === confirming);
  return (
    <Card className="p-4 space-y-3" data-testid="uat-runner-card">
      <p className="font-medium text-sm">One-click UAT runners</p>
      <div className="flex flex-wrap gap-3">
        {RUNNERS.map((r) => (
          <Button key={r.provider} size="sm" variant="outline"
                  disabled={!!running || inFlight.includes(r.provider)}
                  onClick={() => setConfirming(r.provider)}
                  data-testid={`uat-run-${r.provider}-btn`}>
            {running === r.provider ? "Running…" : `Run UAT — ${r.label}`}
          </Button>
        ))}
      </div>
      {running && (
        <p className="text-xs text-blue-700 bg-blue-50 rounded-lg p-2" data-testid="uat-run-progress">
          Running {running} UAT — executing the 11 steps and recording evidence…
        </p>
      )}
      {result && (
        <div className="text-xs bg-gray-50 rounded-lg p-3" data-testid="uat-run-result">
          <p className="font-medium">
            {result.provider}: {result.ran === false
              ? `not run — ${result.reason}`
              : `${result.passed ?? 0}/${result.total ?? 11} passed`}
          </p>
          {result.note && <p className="text-gray-500 mt-1">{result.note}</p>}
        </div>
      )}
      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent data-testid="uat-run-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Run {target?.label} UAT?</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.note} Evidence and timestamps are persisted to the UAT dashboard.
              All live-trading gates remain OFF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="uat-run-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(confirming)} data-testid="uat-run-confirm-btn">
              Run UAT
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function UatTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/ops/uat").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStep = async (provider, step, status) => {
    const evidence = window.prompt(`Evidence for ${provider}/${step} → ${status}`, "") ?? "";
    try {
      await api.post(`/ops/uat/${provider}/${step}`, { status, evidence });
      toast.success("UAT step updated");
      load();
    } catch { toast.error("Update failed"); }
  };
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="ops-uat">
      <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3">{data.notice}</p>
      <UatRunner onDone={load} />
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead><tr><th className="text-left p-2">Provider</th>
            {data.steps.map((s) => <th key={s} className="p-2 text-left">{s.replace(/_/g, " ")}</th>)}
          </tr></thead>
          <tbody>
            {data.providers.map((p) => (
              <tr key={p.provider} className="border-t border-gray-100" data-testid={`uat-row-${p.provider}`}>
                <td className="p-2 font-medium">{p.provider} <span className="text-gray-400">({p.passed}/{p.total})</span></td>
                {data.steps.map((s) => {
                  const st = p.steps[s];
                  return (
                    <td key={s} className="p-2">
                      <button
                        className={`px-2 py-1 rounded-full ${UAT_CLS[st.status]}`}
                        title={`${st.evidence || ""} ${st.updated_at || ""}`}
                        data-testid={`uat-${p.provider}-${s}`}
                        onClick={() => {
                          const order = ["not_tested", "passed", "failed", "blocked"];
                          const next = order[(order.indexOf(st.status) + 1) % order.length];
                          setStep(p.provider, s, next);
                        }}
                      >{st.status.replace("_", " ")}</button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ user_id: "", symbol: "", kind: "", correlation_id: "", order_id: "" });
  const search = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try {
      const { data } = await api.get("/ops/audit/search", { params });
      setRows(data);
    } catch { toast.error("Search failed"); }
  };
  useEffect(() => { search(); }, []); // eslint-disable-line
  return (
    <div className="space-y-4" data-testid="ops-audit">
      <div className="grid md:grid-cols-6 gap-2">
        {Object.keys(f).map((k) => (
          <Input key={k} placeholder={k} value={f[k]} data-testid={`audit-filter-${k}`}
                 onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        ))}
        <Button onClick={search} data-testid="audit-search-btn">Search</Button>
      </div>
      <div className="space-y-2 max-h-[32rem] overflow-auto">
        {rows.map((r) => (
          <Card key={r._id} className="p-3" data-testid="audit-row">
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-brand/10 text-brand border-0">{r.kind}</Badge>
              <span className="text-gray-400">{r.created_at}</span>
              <span className="text-gray-500">actor {r.actor}</span>
              <span className={r.outcome === "reject" || r.severity === "critical" ? "text-red-600" : "text-gray-500"}>{r.outcome}</span>
            </div>
            <pre className="text-[11px] text-gray-600 mt-1 overflow-auto max-h-32">{JSON.stringify(r.context, null, 1)}</pre>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-sm text-gray-500">No events match.</p>}
      </div>
    </div>
  );
}

function ReadinessTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/ops/readiness").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const cycle = async (item) => {
    const order = ["not_started", "in_progress", "done", "blocked"];
    const next = order[(order.indexOf(item.status) + 1) % order.length];
    try {
      await api.patch("/ops/readiness", { category: item.category, item: item.item, status: next });
      load();
    } catch { toast.error("Update failed"); }
  };
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  const cls = { done: "bg-emerald-100 text-emerald-700", in_progress: "bg-blue-100 text-blue-700", blocked: "bg-red-100 text-red-700", not_started: "bg-gray-100 text-gray-500" };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="ops-readiness">
      {Object.entries(data.categories).map(([cat, items]) => (
        <Card key={cat} className="p-4" data-testid={`readiness-${cat}`}>
          <p className="font-medium text-sm mb-2">{cat.replace(/_/g, " ")} <span className="text-gray-400 text-xs">{data.summary[cat].done}/{data.summary[cat].total}</span></p>
          <div className="space-y-1">
            {items.map((it) => (
              <div key={it.item} className="flex items-center justify-between text-xs">
                <span>{it.item}</span>
                <button className={`px-2 py-0.5 rounded-full ${cls[it.status]}`} onClick={() => cycle(it)}
                        data-testid={`readiness-item-${cat}-${items.indexOf(it)}`}>
                  {it.status.replace("_", " ")}
                </button>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Ops() {
  const { user } = useAuth();
  if (user && user.role !== "admin") {
    return <p className="text-sm text-red-600" data-testid="ops-forbidden">403 — admin only.</p>;
  }
  return (
    <div className="space-y-6" data-testid="ops-root">
      <div>
        <h1 className="text-2xl font-display font-semibold">Production Ops</h1>
        <p className="text-sm text-gray-500">Health · Broker UAT · Audit Explorer · Beta Readiness — live trading remains OFF.</p>
      </div>
      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health" data-testid="tab-health">Health</TabsTrigger>
          <TabsTrigger value="uat" data-testid="tab-uat">Broker UAT</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Explorer</TabsTrigger>
          <TabsTrigger value="readiness" data-testid="tab-readiness">Readiness</TabsTrigger>
        </TabsList>
        <TabsContent value="health"><HealthTab /></TabsContent>
        <TabsContent value="uat"><UatTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="readiness"><ReadinessTab /></TabsContent>
      </Tabs>
    </div>
  );
}
